import { LocalDate, LocalTime } from '@js-joda/core';

import { AvailabilityOverlappingFault } from '@dreryk/egabinet-appointment-frontend-api';

import {
   createSingleInterpolatedTranslationKey,
   dateFormatter,
   InterpolatedTranslationKey,
   TranslatableText,
} from '@dreryk/react-common';
import { isDateBefore, isDateBeforeNow } from '@dreryk/react-components';

import { personnelTranslationKeys } from '../../translations';

import { AdjustStartTimeOrEndTime, FormType, RecurrencePeriod, TimePickerRangeType } from './common.types';
import { WEEK_START_DAY } from './common.utils';

export const useDisabledDaySelector = (
   recurrencePeriodSupplier: () => RecurrencePeriod,
   formType: FormType
): { disabledDaySelector: (day: LocalDate) => TranslatableText | undefined } => {
   return {
      disabledDaySelector: (day) => {
         const recurrencePeriod = recurrencePeriodSupplier();
         const isSingle = recurrencePeriod === RecurrencePeriod.NONE;
         const dateIsBeforeCurrentDate = isDateBeforeNow(day);
         const dateIsBeforeCurrentWeek = isDateBefore(day, LocalDate.now().with(WEEK_START_DAY));

         if (!isSingle && dateIsBeforeCurrentDate) {
            return (formType === FormType.CREATION
               ? personnelTranslationKeys.incorrectPastDateForSingleAvailability
               : personnelTranslationKeys.incorrectPastDateForSingleAvailabilityUpdate) as TranslatableText;
         }
         if (isSingle && dateIsBeforeCurrentWeek) {
            return formType === FormType.CREATION
               ? personnelTranslationKeys.incorrectPastDateForRecurringAvailability
               : personnelTranslationKeys.incorrectPastDateForRecurringAvailabilityUpdate;
         }
         return undefined;
      },
   };
};

export const useRecurrencePeriodSupplier = (recurrencePeriod: unknown): (() => RecurrencePeriod) => {
   return (): RecurrencePeriod => {
      return recurrencePeriod as RecurrencePeriod;
   };
};

export const useAdjustStartTimeOrEndTime = (
   getValue: (name: string) => unknown,
   setValue: (name: string, value: LocalTime) => void
): AdjustStartTimeOrEndTime => {
   return (rangeType: TimePickerRangeType, value: LocalTime, stepInMinutes: number): void => {
      const startTime = getValue('startTime') as LocalTime;
      const endTime = getValue('endTime') as LocalTime;

      if (rangeType === TimePickerRangeType.START && LocalTime.MIDNIGHT.equals(value)) {
         setValue('endTime', endTime.minusMinutes(stepInMinutes));
         return;
      }

      if (rangeType === TimePickerRangeType.START && !value.isAfter(startTime)) {
         setValue('startTime', endTime.minusMinutes(stepInMinutes));
      }
      if (rangeType === TimePickerRangeType.END && !value.isBefore(endTime)) {
         setValue('endTime', startTime.plusMinutes(stepInMinutes));
      }
   };
};

export const useCreateOverlappingAvailabilityErrorMessage = (): ((
   error: AvailabilityOverlappingFault
) => InterpolatedTranslationKey) => {
   return (fault) => {
      return createSingleInterpolatedTranslationKey(
         personnelTranslationKeys.overlappingAvailabilityError,
         'conflictDate',
         dateFormatter(fault.overlappingAvailabilityDates[0])
      );
   };
};
